import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { Anchor, Box, Label, LabeledControl, Switch, Text, TextButton } from '@zillow/constellation-10';

import { useGetListingDetails, useGetTours } from '../../../../../hooks/queries/properties';
import accessUtils from '../../../../../utils/accessUtils';
import propertyUtils from '../../../../../utils/propertyUtils';

import TourCardEditable from '../../../../PostingPath/MediaContent/ListingTourFormField/components/TourCardEditable';
import TourCardEmpty from '../../../../PostingPath/MediaContent/ListingTourFormField/components/TourCardEmpty';

import VideoFormField from '../../../../PostingPath/MediaContent/VideoFormField';

import type { ToastRootPropsInterface } from '@zillow/constellation-10';
import type { GetUserPropertyDetailsFloorPlanUnitWebDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  isDisabled?: boolean;
  canEditTourOverwrite?: boolean;
  onToggleActive: () => void;
  id: string;
  floorPlanIndex: number;
  unit: GetUserPropertyDetailsFloorPlanUnitWebDto;
  unitIndex: number;
  updateStatusMessage: (message: string, status: ToastRootPropsInterface['tone']) => void;
  handleEditUnit: () => void;
};

const UnitCard = ({
  canEditTourOverwrite = true,
  floorPlanIndex,
  id,
  isDisabled = false,
  onToggleActive,
  unit,
  unitIndex,
  updateStatusMessage,
  handleEditUnit,
}: Props) => {
  const router = useRouter();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { floorplans, units } = useGetTours(listingAlias, listingDetails);
  const unitWithTours = floorPlanIndex !== undefined ? floorplans[floorPlanIndex].units[unitIndex] : units[unitIndex];

  const hasAnalyticsRentComps = listingDetails?.hasAnalyticsRentComps ?? false;
  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;

  const date = isValid(new Date(unit.dateAvailable)) ? format(new Date(unit.dateAvailable), 'MM/dd/yyyy') : '-';
  const price = propertyUtils.formatFloorPlanDisplayPrice(unit.lowPrice, unit.highPrice);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const renderAnalyze = () => {
    return (
      <>
        &nbsp;
        <Anchor target="_blank" href="https://www.zillow.com/rental-manager/price-my-rental/">
          Analyze
        </Anchor>
      </>
    );
  };

  return (
    <Box
      css={{
        border: '1px solid {colors.border.subtle}',
        borderBottom: '3px solid {colors.border.accent.brand.hero}',
        borderRadius: '100',
        backgroundColor: 'bg.neutral',
      }}
      data-testid="UnitCard"
    >
      <Box css={{ padding: '400' }}>
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '400' }}>
          <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 6' }}>
            <Text textStyle="body-bold" css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {unit.name || 'Unit name'}
            </Text>
          </Box>
          <Box css={{ gridColumnStart: '7', gridColumnEnd: 'span 6' }}>
            <Box
              css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', md: { justifyContent: 'flex-end' } }}
            >
              <LabeledControl
                controlId={`Switch-${id}`}
                label={
                  <Label
                    css={{
                      textStyle: 'body-bold !important',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Unit available
                  </Label>
                }
                control={<Switch checked={unit.active} onChange={onToggleActive} disabled={isDisabled} />}
                css={{ flexDirection: 'row-reverse', gap: '400' }}
              />
            </Box>
          </Box>
          <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 12' }}>
            <Text css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {canAccessTotalMonthlyPriceWithFee ? 'Base rent' : 'Price'}&nbsp;
              <b>{`${price || '-'} `}</b>
              {hasAnalyticsRentComps && renderAnalyze()}| sqft&nbsp;
              <b>{unit?.squareFeet || '-'}</b>
              &nbsp;| Avail.&nbsp;
              <b>{date}</b>
            </Text>
          </Box>
          <Box css={{ gridColumnStart: '1', gridColumnEnd: { base: 'span 12', lg: 'span 6' } }}>
            <Box css={{ marginBottom: '200' }}>
              <Text textStyle="body-bold">3D tour</Text>
            </Box>
            {!unitWithTours.vrModel?.vrModelId && !unitWithTours?.externalTour?.tourUrl ? (
              <TourCardEmpty
                tourType="unit"
                isDisabled={canEditTourOverwrite ? false : isDisabled}
                floorPlanIndex={floorPlanIndex}
                listingToursCount={0}
                unitIndex={unitIndex}
              />
            ) : (
              <TourCardEditable
                vrModel={{ ...unitWithTours.vrModel, creatorZuid: Number(unitWithTours.vrModel?.creatorZuid) }}
                externalTour={unitWithTours.externalTour}
                tourType="unit"
                hasBigBottomBorder={false}
                isDisabled={canEditTourOverwrite ? false : isDisabled}
                floorPlanIndex={floorPlanIndex}
                unitIndex={unitIndex}
                updateStatusMessage={updateStatusMessage}
              />
            )}
          </Box>
          <Box css={{ gridColumnStart: { base: '1', lg: '7' }, gridColumnEnd: { base: 'span 12', lg: 'span 6' } }}>
            <Box css={{ marginBottom: '200' }}>
              <Text textStyle="body-bold">Video</Text>
            </Box>
            <VideoFormField
              unitId={unit.id}
              unitVideoUrl={unit.videoUrl}
              isDisabled={canEditTourOverwrite ? false : isDisabled}
            />
          </Box>
        </Box>
      </Box>

      {!isDisabled && (
        <Box
          css={{
            borderTop: '1px solid {colors.border.subtle}',
            paddingBlock: '400',
          }}
        >
          <Box css={{ marginLeft: '200' }}>
            <TextButton aria-label="Edit unit" tabIndex={0} onClick={handleEditUnit}>
              Edit
            </TextButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UnitCard;
